import { useState, useEffect } from 'react';

const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
const DEFAULT_ANDROID_KEYBOARD_HEIGHT = 400;

const useTextareaHeight = (
  inputRef: React.RefObject<HTMLTextAreaElement>,
  scrollDivRef: React.RefObject<HTMLDivElement>,
) => {
  const [textareaHeight, setTextareaHeight] = useState<number>(64 - 22);
  const [originalScrollHeight, setOriginalScrollHeight] = useState<number | null>(null);
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const [prevViewportHeight, setPrevViewportHeight] = useState<number>(window.innerHeight);

  const adjustHeight = (withScroll = false) => {
    if (!inputRef.current) return;

    inputRef.current.style.height = '42px';
    const newHeight = Math.min(inputRef.current.scrollHeight, 108);
    inputRef.current.style.height = `${newHeight}px`;
    setTextareaHeight(newHeight);

    if (window.visualViewport && scrollDivRef.current) {
      const visualHeight = window.visualViewport.height;
      const offsetTop = scrollDivRef.current.getBoundingClientRect().top;
      const scrollDivHeight = visualHeight - offsetTop - newHeight - 16;

      if (originalScrollHeight === null) {
        setOriginalScrollHeight(scrollDivRef.current.clientHeight);
      }

      scrollDivRef.current.style.height = `${scrollDivHeight}px`;

      if (withScroll) {
        const keyboardHeight = window.innerHeight - visualHeight - window.visualViewport.offsetTop;

        requestAnimationFrame(() => {
          scrollDivRef.current?.scrollBy({
            top: keyboardHeight,
            behavior: 'smooth',
          });
        });
      }

      window.visualViewport.onscroll = () => {
        window.scrollTo(0, 0);
      };
    }
  };

  const restoreHeight = () => {
    if (scrollDivRef.current && originalScrollHeight !== null) {
      scrollDivRef.current.style.height = `${originalScrollHeight}px`;
    }
  };

  useEffect(() => {
    adjustHeight();

    const handleResize = () => {
      if (!window.visualViewport || !scrollDivRef.current) return;

      const currentHeight = window.visualViewport.height;
      const isClosing = currentHeight === window.innerHeight;

      if (isIOS) {
        if (isClosing) {
          restoreHeight();
          setIsKeyboardOpen(false);
        } else {
          if (!isKeyboardOpen) {
            setIsKeyboardOpen(true);
            adjustHeight(true);
          } else {
            adjustHeight(false);
          }
        }
      } else {
        const keyboardOpened = currentHeight < prevViewportHeight;
        if (keyboardOpened) {
          const keyboardHeight = DEFAULT_ANDROID_KEYBOARD_HEIGHT;

          scrollDivRef.current?.scrollBy({
            top: keyboardHeight,
            behavior: 'smooth',
          });
        } else if (isClosing) {
          restoreHeight();
        }

        setPrevViewportHeight(currentHeight);
      }
    };

    window.visualViewport?.addEventListener('resize', handleResize);
    return () => {
      window.visualViewport?.removeEventListener('resize', handleResize);
    };
  }, []);

  return { textareaHeight, adjustHeight };
};

export default useTextareaHeight;
